import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useData } from '../context/DataContext';

function MediaItem({ item }) {
  if (!item) return null;
  const isIg = item.src && (item.src.includes('instagram') || item.src.includes('instagr.am'));
  return (
    <figure className="media-item">
      {item.type === 'image' && (
        <img loading="lazy" decoding="async" src={item.src} alt={item.caption || ''} />
      )}
      {item.type === 'video' && (
        <video controls playsInline>
          <source src={item.src} />
        </video>
      )}
      {item.type === 'embed' && (
        <iframe
          className={isIg ? 'embed-ig' : ''}
          src={item.src}
          title={item.caption || 'Embedded media'}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      )}
      {item.caption && <figcaption>{item.caption}</figcaption>}
    </figure>
  );
}

export default function WorkDetailPage() {
  const { id } = useParams();
  const { data, loading } = useData();
  const [toastVisible, setToastVisible] = useState(false);

  useEffect(() => {
    document.body.classList.add('page-work-detail');
    return () => document.body.classList.remove('page-work-detail');
  }, []);

  const project = data?.work?.find(w => w.id === id);
  const allWork = data?.work || [];
  const currentIndex = allWork.findIndex(w => w.id === id);
  const prevProject = currentIndex > 0 ? allWork[currentIndex - 1] : null;
  const nextProject = currentIndex < allWork.length - 1 ? allWork[currentIndex + 1] : null;

  useEffect(() => {
    if (project) document.title = `${project.title} — Preeyanka Mondal`;
  }, [project]);

  if (loading) return null;

  if (!project) {
    return (
      <div className="wrap">
        <div className="not-found" style={{ padding: '80px 0' }}>
          <h2>Project not found</h2>
          <p><Link to="/work" style={{ color: 'var(--accent)' }}>← Back to Work</Link></p>
        </div>
      </div>
    );
  }

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setToastVisible(true);
      setTimeout(() => setToastVisible(false), 2200);
    } catch (_) {}
  };

  const credits = project.credits || {};
  const hasCredits = Object.keys(credits).length > 0;

  return (
    <div className="work-root">
      <div className="wrap">
        <div className="work-layout">
          {/* Left: content */}
          <div className="work-content">
            <h1 className="case-title">{project.title}</h1>
            <p className="case-sub">{project.subtitle}</p>

            <div className="share-row">
              <button className="btn" onClick={handleShare}>
                <i className="fa-solid fa-link"></i> Copy link
              </button>
              {project.links?.live && (
                <a className="btn" href={project.links.live} target="_blank" rel="noopener noreferrer">
                  <i className="fa-solid fa-arrow-up-right-from-square"></i> View live
                </a>
              )}
            </div>

            <div
              className="case-body"
              dangerouslySetInnerHTML={{ __html: project.body_html || '' }}
            />

            {hasCredits && (
              <div style={{ marginTop: 28, fontSize: 13, color: 'var(--muted)' }}>
                <strong style={{ color: 'var(--text)', display: 'block', marginBottom: 6 }}>Credits</strong>
                {credits.cw && <div>CW: {credits.cw}</div>}
                {credits.ad && <div>AD: {credits.ad}</div>}
                {credits.production && <div>Production: {credits.production}</div>}
              </div>
            )}
          </div>

          {/* Right: media */}
          <div className="work-media">
            {(project.media || []).map((item, i) => (
              <MediaItem key={i} item={item} />
            ))}
          </div>
        </div>
      </div>

      {/* Side nav */}
      <nav className="ext-nav" aria-label="Project navigation">
        <Link to="/work" className="ext-nav-btn" title="All work" aria-label="All work">
          <i className="fa-solid fa-grid-2"></i>
        </Link>
        {prevProject && (
          <Link
            to={`/work/${prevProject.id}`}
            className="ext-nav-btn"
            title={prevProject.title}
            aria-label={`Previous: ${prevProject.title}`}
          >
            <i className="fa-solid fa-chevron-up"></i>
          </Link>
        )}
        {nextProject && (
          <Link
            to={`/work/${nextProject.id}`}
            className="ext-nav-btn"
            title={nextProject.title}
            aria-label={`Next: ${nextProject.title}`}
          >
            <i className="fa-solid fa-chevron-down"></i>
          </Link>
        )}
      </nav>

      {/* Share toast */}
      <div className={`share-toast${toastVisible ? ' visible' : ''}`} aria-live="polite">
        Link copied!
      </div>
    </div>
  );
}
